/**
 * @fileoverview Civil & Criminal Offences — focused single-head view
 * @module jobs/phases/review/civilCriminalOffences/OffenceFocusedHead
 *
 * Mirrors the flagged FocusedHeadView: scope strip (window / insolvency /
 * recoverable / last run), statutory-window callout, per-respondent exposure,
 * the findings table, an editable details section, the parked proposed-summary,
 * the unified agent feed, and the output history.
 */

import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  GitPullRequestArrow,
  Loader2,
  Lock,
  LockOpen,
  Pencil,
  RefreshCw,
  X,
} from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
  type ChangeHistoryEntry,
} from "@/components/jobs/phases/gather/summarization/shared-panels/ChangeHistory";
import { UnifiedFeed } from "@/components/jobs/phases/gather/summarization/shared-panels";
import { Callout } from "../flaggedTransactions/Callout";
import {
  AgentText,
  AgentProse,
  StatuteBadge,
} from "../flaggedTransactions/AgentText";
import { DutyContextBanner } from "../DutyContextBanner";
import { displayProvision } from "../duties";
import { OffenceFindingsTable } from "./OffenceFindingsTable";
import { AgentStatusBadge } from "../flaggedTransactions/AgentStatusBadge";
import {
  agentText,
  fmtDate,
  fmtDateTime,
  fmtMoney,
  humaniseTokens,
  INSOLVENCY_BASIS_LABEL,
  OFFENCE_NO_INSOLVENCY_ELEMENT_NOTE,
  offenceOutcomeDisplay,
  type OffenceHeadUI,
  type OffenceWorkspaceUI,
} from "./offenceShared";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";

const offences = api.jobs.summarization.agents.civilCriminalOffences;

function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <div className="inline-flex items-baseline gap-1.5 rounded-md border bg-muted/40 px-2.5 py-1">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      <span className="text-xs font-medium tabular-nums">{value}</span>
    </div>
  );
}

const OFFENCE_OUTCOMES = [
  "flags_identified",
  "no_flags",
  "insufficient_data",
  "blocked_missing_inputs",
] as const;

export function OffenceFocusedHead(props: {
  jobId: Id<"jobs">;
  head: OffenceHeadUI;
  isViewer: boolean;
  insolvency: OffenceWorkspaceUI["insolvency"];
  onBack: () => void;
}) {
  const { jobId, head, isViewer, insolvency, onBack } = props;

  const reRunHeads = useMutation({
    mutationFn: useConvexMutation(offences.publicMutations.reRunHeads),
  });
  const setHeadLock = useMutation({
    mutationFn: useConvexMutation(offences.publicMutations.setHeadLock),
  });
  const editHeadOutput = useMutation({
    mutationFn: useConvexMutation(offences.publicMutations.editHeadOutput),
  });
  const resolveProposed = useMutation({
    mutationFn: useConvexMutation(
      offences.publicMutations.resolveProposedSummary,
    ),
  });

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    outcome: head.outcome,
    headline: head.headline,
    reasoning: head.reasoning,
  });

  const startEditing = () => {
    setDraft({
      outcome: head.outcome,
      headline: head.headline,
      reasoning: head.reasoning,
    });
    setEditing(true);
  };
  const saveEdits = () => {
    editHeadOutput.mutate(
      {
        jobId,
        headKey: head.headKey as never,
        updates: {
          outcome: draft.outcome,
          headline: draft.headline.trim() || undefined,
          reasoning: draft.reasoning,
        },
      },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success(
            head.locked
              ? "Output updated"
              : "Output updated — lock the head to stop the next run overwriting your edit",
          );
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
      },
    );
  };

  const { data: taskDetail } = useQuery(
    convexQueryOrIdle(
      api.jobs.summarization.publicQueries.getSummarizationTaskDetail,
      head.taskId ? { taskId: head.taskId } : undefined,
    ),
  );

  const historyUserIds = useMemo<Array<Id<"users">>>(() => {
    const ids = new Set<Id<"users">>();
    for (const c of head.changeHistory)
      if (c.userId) ids.add(c.userId as Id<"users">);
    return Array.from(ids);
  }, [head.changeHistory]);
  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, { ids: historyUserIds }),
    enabled: historyUserIds.length > 0,
  });
  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) map.set(u._id, u);
    return map;
  }, [historyUsers]);

  // Heads made out without proving insolvency: the chip says so rather than
  // presenting the case-wide date as something this claim turns on.
  const noInsolvencyElement = OFFENCE_NO_INSOLVENCY_ELEMENT_NOTE[head.headKey];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
          className="gap-1.5"
        >
          <ArrowLeft className="h-4 w-4" /> All heads
        </Button>
        {!isViewer && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() =>
                reRunHeads
                  .mutateAsync({ jobId, headKeys: [head.headKey as never] })
                  .then(() => toast.success("Re-running this head"))
              }
            >
              <RefreshCw className="h-4 w-4" /> Re-run
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() =>
                setHeadLock.mutateAsync({
                  jobId,
                  headKey: head.headKey as never,
                  locked: !head.locked,
                })
              }
            >
              {head.locked ? (
                <Lock className="h-4 w-4" />
              ) : (
                <LockOpen className="h-4 w-4" />
              )}
              {head.locked ? "Locked" : "Lock"}
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-border bg-background p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="mb-3 flex flex-wrap items-center gap-2 border-b pb-3">
          <h2 className="text-base font-semibold">{head.name}</h2>
          <AgentStatusBadge
            status={head.status}
            basisNote={head.reviewWindow.basisNote}
          />
          <StatuteBadge>
            {displayProvision("offences", head.headKey, head.provision)}
          </StatuteBadge>
        </div>

        <DutyContextBanner
          jobId={jobId}
          workspace="offences"
          headKey={head.headKey}
          className="mb-3"
        />

        <div className="flex flex-wrap gap-2">
          <StatChip
            label="Window"
            value={`${head.reviewWindow.start ? fmtDate(head.reviewWindow.start) : "open"} → ${head.reviewWindow.end ? fmtDate(head.reviewWindow.end) : "—"}`}
          />
          <div
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 ${
              noInsolvencyElement ? "border-dashed" : "bg-muted/40"
            }`}
            title={noInsolvencyElement ?? agentText(insolvency.note)}
          >
            {noInsolvencyElement ? (
              <>
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Insolvency
                </span>
                <span className="text-xs font-medium">Not an element</span>
                <span className="text-[10px] text-muted-foreground">
                  (case-wide:{" "}
                  {insolvency.date ? fmtDate(insolvency.date) : "unavailable"})
                </span>
              </>
            ) : (
              <>
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  Est Insolvency Date
                </span>
                <span className="text-xs font-medium tabular-nums">
                  {insolvency.date ? fmtDate(insolvency.date) : "unavailable"}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  (
                  {INSOLVENCY_BASIS_LABEL[insolvency.basis] ??
                    insolvency.basis.replace(/_/g, " ")}
                  )
                </span>
              </>
            )}
          </div>
          {head.hasQuantum && head.decisions.recoverableValue > 0 && (
            <StatChip
              label="Est Recoverable"
              value={fmtMoney(head.decisions.recoverableValue)}
            />
          )}
          {!head.hasQuantum && head.referralCount > 0 && (
            <StatChip label="Referrals" value={String(head.referralCount)} />
          )}
          <StatChip label="Last run" value={fmtDateTime(head.lastRunAt)} />
        </div>

        <Callout tone="legal" title="Statutory window" className="mt-3">
          <AgentText text={head.reviewWindow.basisNote} />
        </Callout>

        <div className="mt-3 border-t pt-3">
          <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Findings
          </h4>
          <OffenceFindingsTable jobId={jobId} head={head} isViewer={isViewer} />
        </div>

        {/* Details — outcome + reasoning, practitioner-editable */}
        <div className="group/output mt-3 border-t pt-3">
          <Collapsible defaultOpen>
            <CollapsibleTrigger className="group flex w-full items-center justify-between">
              <div className="flex items-center gap-2">
                <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                  Details
                </h4>
                {(() => {
                  const d = offenceOutcomeDisplay(head);
                  return (
                    <Badge color={d.color} variant="soft" size="sm">
                      {d.label}
                    </Badge>
                  );
                })()}
              </div>
              <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              {editing ? (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <select
                      value={draft.outcome}
                      onChange={(e) =>
                        setDraft((d) => ({
                          ...d,
                          outcome: e.target.value as typeof d.outcome,
                        }))
                      }
                      className="h-8 rounded-md border bg-background px-2 text-xs"
                    >
                      {OFFENCE_OUTCOMES.map((o) => (
                        <option key={o} value={o}>
                          {o.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                    <div className="ml-auto flex items-center gap-1.5">
                      <Button
                        size="sm"
                        className="h-8 gap-1.5"
                        disabled={editHeadOutput.isPending}
                        onClick={saveEdits}
                      >
                        {editHeadOutput.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Check className="h-3.5 w-3.5" />
                        )}
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 gap-1.5"
                        onClick={() => setEditing(false)}
                      >
                        <X className="h-3.5 w-3.5" /> Cancel
                      </Button>
                    </div>
                  </div>
                  <Input
                    value={draft.headline}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, headline: e.target.value }))
                    }
                    className="text-sm font-medium"
                  />
                  <Textarea
                    value={draft.reasoning}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, reasoning: e.target.value }))
                    }
                    rows={8}
                    className="resize-y text-sm leading-relaxed"
                  />
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-2">
                    <p className="mt-1 flex-1 text-sm font-medium leading-relaxed">
                      {head.headline ? (
                        <AgentText text={head.headline} />
                      ) : (
                        "Not yet assessed."
                      )}
                    </p>
                    {!isViewer && (
                      <button
                        type="button"
                        className="mt-1 shrink-0 rounded p-1 text-muted-foreground/0 transition-colors hover:bg-muted hover:text-foreground group-hover/output:text-muted-foreground"
                        title="Edit the agent's status, conclusion and reasoning"
                        onClick={startEditing}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  {head.reasoning && (
                    <AgentProse
                      text={humaniseTokens(head.reasoning)}
                      className="mt-2 text-sm leading-relaxed text-muted-foreground"
                    />
                  )}
                </>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Parked proposed summary */}
        {head.proposedSummary && (
          <div className="mt-3 rounded-lg border border-violet-300 bg-violet-50/60 p-3 dark:border-violet-800 dark:bg-violet-950/20">
            <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-violet-700 dark:text-violet-300">
              <GitPullRequestArrow className="h-3.5 w-3.5" />
              Proposed output update (
              {fmtDateTime(head.proposedSummary.proposedAt)})
            </div>
            <p className="text-xs">
              <AgentText
                text={String(head.proposedSummary.after.headline ?? "")}
              />
            </p>
            {!isViewer && (
              <div className="mt-2 flex gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 text-xs"
                  onClick={() =>
                    resolveProposed
                      .mutateAsync({
                        jobId,
                        headKey: head.headKey as never,
                        accept: true,
                      })
                      .then(() => toast.success("Update applied"))
                  }
                >
                  <Check className="h-3 w-3" /> Agree
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 text-xs"
                  onClick={() =>
                    resolveProposed
                      .mutateAsync({
                        jobId,
                        headKey: head.headKey as never,
                        accept: false,
                      })
                      .then(() => toast.success("Update dismissed"))
                  }
                >
                  <X className="h-3 w-3" /> Disagree
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Unified agent feed */}
        {taskDetail && (
          <div className="mt-3 border-t pt-3">
            <Collapsible defaultOpen={head.status === "updating"}>
              <CollapsibleTrigger className="group flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <h4 className="font-sans text-xs font-semibold uppercase tracking-wider text-[#121212]">
                    Agent Feed
                  </h4>
                  {head.status === "updating" && (
                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                  )}
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                <UnifiedFeed
                  task={taskDetail}
                  subAgents={[]}
                  taskTree={
                    (taskDetail.workingMemory as { taskTree?: unknown[] })
                      ?.taskTree as never
                  }
                  isRunning={head.status === "updating"}
                />
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {/* Output history */}
        {head.changeHistory.length > 0 && (
          <div className="mt-3 border-t pt-3">
            <ChangeHistory
              history={head.changeHistory as ChangeHistoryEntry[]}
              usersById={historyUsersById}
              title="History"
            />
          </div>
        )}

        <AiDisclaimer className="mt-4" />
      </div>
    </div>
  );
}
